'use client';

import { useState } from "react";
import Swal from "sweetalert2";
import { uploadToS3, submitPDPLRequest } from '@/apis';

export default function DSARPage() {
	const [data, setData] = useState({
		fullName: "",
		email: "",
		mobile: "",
		customerId: "",
		identityFile: null,
		supportingFiles: [],
		rights: [],
		description: ""
	});

	const handleChange = (e) => {
		const { name, type, value, checked, files } = e.target;

		if (type === "checkbox") {
			setData((prev) => {
				const newArray = checked
					? [...prev.rights, value]
					: prev.rights.filter((v) => v !== value);
				return { ...prev, rights: newArray };
			});
		} else if (type === "file") {
			if (name === "identityFile") {
				setData((prev) => ({ ...prev, identityFile: files[0] }));
			} else if (name === "supportingFiles") {
				setData((prev) => ({ ...prev, supportingFiles: Array.from(files) }));
			}
		} else {
			setData((prev) => ({ ...prev, [name]: value }));
		}
	};

	

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!data.identityFile) {
			return Swal.fire("Error", "Please upload identity document.", "error");
		}

		try {
           // Upload identity document
            const identityFormData = new FormData();
            identityFormData.append("file", data.identityFile);
            const identityDoc = await uploadToS3(identityFormData);
            // setIdentityFile({ name: data.identityFile.name, url: identityDoc.fileUrl });

            // Upload supporting documents
            const supportingDocs = await Promise.all(
                data.supportingFiles.map((file) => {
                    const supportFormData = new FormData();
                    supportFormData.append("file", file);
                    return uploadToS3(supportFormData);
                })
            );

			const payload = {
				formType: "DSAR",
				applicantInformation: {
					fullName: data.fullName,
					email: data.email,
					phone: data.mobile,
					customerId: data.customerId,
				},
				identityDocuments: [identityDoc],
				supportingDocuments: supportingDocs,
				dataToBeRectified: [],
				dsarRequestTypes: data.rights,
				requestDescription: data.description
			};

			const res = await submitPDPLRequest(payload);

			Swal.fire({
				icon: "success",
				title: "Request Submitted",
				html: `${res.message}<br>Case ID: <b style="color:blue">${res.caseId}</b>`,
				timer: 6000
			});
		} catch (err) {
			console.error(err);
			Swal.fire("Error", "Something went wrong.", "error");
		}
	};

	return (
		<section className='legacy-page bg-gray-50 text-gray-900 px-6 py-6 md:py-10'>
			<div className='container mx-auto max-w-4xl lg:max-w-5xl'>
				<form className='space-y-8' onSubmit={handleSubmit}>
					{/* Header */}
					<h1 className='text-3xl md:text-4xl font-bold text-primary mb-8'>
						Data Subject Rights Request (PDPL)
					</h1>

					{/* A. User Identification Fields */}
					<section className='mb-8'>
						<h2 className='text-lg font-semibold mb-2'>A. User Identification Fields</h2>
						<p className='text-gray-600 mb-4'>PDPL requires reasonable measures to verify identity.</p>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div className='flex flex-col'>
								<label className='mb-1 font-medium'>Full Name <span className='text-red-500'>*</span></label>
								<input
									name="fullName"
									value={data.fullName}
									onChange={handleChange}
									className='border p-2 rounded'
									placeholder='Full Name (as per Emirates ID/Passport) *'
									required
								/>
							</div>
							<div className='flex flex-col'>
								<label className='mb-1 font-medium'>Registered Email Address <span className='text-red-500'>*</span></label>
								<input
									name="email"
									value={data.email}
									onChange={handleChange}
									className='border p-2 rounded'
									type='email'
									placeholder='Registered Email Address Linked to Account'
									required
								/>
							</div>
							<div className='flex flex-col'>
								<label className='mb-1 font-medium'>Registered Mobile Number <span className='text-red-500'>*</span></label>
								<input
									name="mobile"
									value={data.mobile}
									onChange={handleChange}
									className='border p-2 rounded'
									placeholder='Registered Mobile Number Linked to Account'
									required
								/>
							</div>
							<div className='flex flex-col'>
								<label className='mb-1 font-medium'>Customer ID / Account ID</label>
								<input
									name="customerId"
									value={data.customerId}
									onChange={handleChange}
									className='border p-2 rounded'
									placeholder='Optional'
								/>
							</div>
						</div>
					</section>

					{/* B. Verification Document Upload */}
					<section className='mb-8'>
						<h2 className='text-lg font-semibold mb-2'>B. Verification Document Upload</h2>
						<p className='text-gray-700 mb-3'>Upload <b> one mandatory </b> document for identity verification.</p>
						<ul className='list-disc list-inside text-gray-700 mb-4'>
							<li>Emirates ID (Front + Back)</li>
							<li>OR Passport Photo Page</li>
							<li>Allowed: JPG, PNG, PDF — Max size: 5MB</li>
						</ul>
						<label className='inline-block bg-primary text-white px-4 py-2 rounded cursor-pointer'>
							Upload Document *
							<input
								type='file'
								name="identityFile"
								className='hidden'
								accept='.jpg,.jpeg,.png,.pdf'
								onChange={handleChange}
								required
							/>
						</label>
						{data.identityFile && <p className='mt-2 text-gray-700'>{data.identityFile.name}</p>}
					</section>

					{/* C. Data Subject Rights */}
					<section className="mb-8">
						<h2 className="text-xl font-medium mb-3">C. Data Subject Rights</h2>
						<p className="text-gray-600 mb-3">Select the rights you want to exercise:</p>
						<fieldset className="grid grid-cols-1 md:grid-cols-2 gap-3">
							{['access','rectify','delete','restrict','optout','withdraw','portability','object_automated'].map((r) => (
								<label key={r} className="flex items-center gap-2">
									<input
										type="checkbox"
										name="rights"
										value={r}
										checked={data.rights.includes(r)}
										onChange={handleChange}
										className="w-4 h-4"
									/>
									<span>{r.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
								</label>
							))}
						</fieldset>
					</section>

					{/* D. Additional Request Description */}
					<section className='mb-8'>
						<h2 className='text-lg font-semibold mb-2'>D. Additional Request Description</h2>
						<textarea
							name="description"
							value={data.description}
							onChange={handleChange}
							className='border p-2 rounded w-full'
							rows={4}
							placeholder='Describe your request in detail'
						/>
					</section>

					{/* Supporting Documents */}
					<section className='mb-8'>
						<h2 className='font-medium mb-2'>Upload Supporting Documents <span className='text-gray-500'>(optional)</span></h2>
						<label className='flex items-center justify-center gap-2 bg-white border border-primary text-primary px-4 py-2 rounded cursor-pointer hover:bg-primary hover:text-white transition w-fit'>
							<span>Upload Documents</span>
							<input
								type='file'
								name="supportingFiles"
								className='hidden'
								accept='.jpg,.jpeg,.png,.pdf'
								multiple
								onChange={handleChange}
							/>
						</label>
						{data.supportingFiles.length > 0 && (
							<ul className="mt-2 text-red-700">
								{data.supportingFiles.map((f,i) => <li key={i}>{f.name}</li>)}
							</ul>
						)}
						<p className='text-xs text-gray-500 mt-2'>Allowed: JPG, PNG, PDF — Max 5MB each</p>
					</section>

					{/* Buttons */}
					<div className='flex gap-4 mt-8'>
						<button type="submit" className='bg-primary text-white px-6 py-2 rounded-md'>Submit DSAR Request</button>
						<button type="button" className='bg-gray-300 px-6 py-2 rounded-md' onClick={()=>window.location.reload()}>Cancel</button>
					</div>
				</form>
			</div>
		</section>
	);
}
